import { z } from 'zod'
import { ghlOpportunityStatusSchema } from '../enums/ghl-opportunity-status.enum.js'
import { ghlAppointmentStatusSchema } from '../enums/ghl-appointment-status.enum.js'

/**
 * Lead stats from GoHighLevel: how a company defines its numbers, and what a
 * sub-account offers to define them from. See docs/gohighlevel-stats.spec.md,
 * "Pipelines" and "Numbers". The numbers themselves are in
 * `lead-stats-numbers.dto.ts`.
 */

/** The longest name a company gives a pipeline, number or conversion. */
const LEAD_STATS_NAME_MAX = 60
/** The longest GoHighLevel name a definition stores. */
const GHL_NAME_MAX = 200
/** How many GoHighLevel names one list in a definition holds. */
const LEAD_STATS_NAMES_MAX = 100
/** How many named pipelines a company keeps. */
export const LEAD_STATS_PIPELINES_MAX = 10
/** How many numbers and conversions a company keeps, together. */
export const LEAD_STATS_METRICS_MAX = 20

/**
 * Where an organization admin turns the lead-stats email off. The mail and the
 * sender both name it, so the words live here.
 */
const LEAD_STATS_REPORT_OFF_SWITCHES = ['gohighlevel', 'pipeline'] as const
export const leadStatsReportOffSwitchSchema = z.enum(LEAD_STATS_REPORT_OFF_SWITCHES)
export type LeadStatsReportOffSwitch = z.infer<typeof leadStatsReportOffSwitchSchema>

/** A name as GoHighLevel shows it: a stage, pipeline, tag, source or calendar. */
export const ghlNameSchema = z.string().trim().min(1).max(GHL_NAME_MAX)
const ghlNameListSchema = z.array(ghlNameSchema).max(LEAD_STATS_NAMES_MAX)

/** A company's own name for a pipeline, number or conversion. */
export const leadStatsNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(LEAD_STATS_NAME_MAX)

/**
 * A number that counts couples in a pipeline. Every list left empty means "any":
 * a definition with all of them empty counts every lead, which is "Leads".
 */
export const leadsMetricDefinitionSchema = z.object({
  /** On any of these stages, by name. */
  stages: ghlNameListSchema.default([]),
  /** With any of these results. */
  results: z.array(ghlOpportunityStatusSchema).max(4).default([]),
  /** Whose contact has any of these tags. */
  tagsAny: ghlNameListSchema.default([]),
  /** Whose contact has none of these tags. */
  tagsNone: ghlNameListSchema.default([]),
  /** Whose lead source is any of these. */
  sources: ghlNameListSchema.default([]),
})
export type LeadsMetricDefinition = z.infer<typeof leadsMetricDefinitionSchema>

/** Which of an appointment's days puts it in a period. */
const APPOINTMENT_COUNT_BYS = ['booked', 'held'] as const
export const appointmentCountBySchema = z.enum(APPOINTMENT_COUNT_BYS)

/** A number that counts appointments on chosen calendars. No statuses means any. */
export const appointmentsMetricDefinitionSchema = z.object({
  calendars: z.array(ghlNameSchema).min(1).max(LEAD_STATS_NAMES_MAX),
  statuses: z.array(ghlAppointmentStatusSchema).max(6).default([]),
  countBy: appointmentCountBySchema,
})
export type AppointmentsMetricDefinition = z.infer<
  typeof appointmentsMetricDefinitionSchema
>

/** A conversion: the total of some numbers divided by the total of others. */
export const conversionDefinitionSchema = z.object({
  numerator: z.array(z.string().uuid()).min(1).max(LEAD_STATS_METRICS_MAX),
  denominator: z.array(z.string().uuid()).min(1).max(LEAD_STATS_METRICS_MAX),
})
export type ConversionDefinition = z.infer<typeof conversionDefinitionSchema>

/** One of a company's numbers or conversions, by kind. */
export const leadStatsMetricDtoSchema = z.discriminatedUnion('kind', [
  z.object({
    id: z.string().uuid(),
    name: leadStatsNameSchema,
    kind: z.literal('leads'),
    definition: leadsMetricDefinitionSchema,
  }),
  z.object({
    id: z.string().uuid(),
    name: leadStatsNameSchema,
    kind: z.literal('appointments'),
    definition: appointmentsMetricDefinitionSchema,
  }),
  z.object({
    id: z.string().uuid(),
    name: leadStatsNameSchema,
    kind: z.literal('conversion'),
    definition: conversionDefinitionSchema,
  }),
])
export type LeadStatsMetricDto = z.infer<typeof leadStatsMetricDtoSchema>

/** A company's named pipeline and the GoHighLevel pipelines it is made of. */
export const leadStatsPipelineDtoSchema = z.object({
  id: z.string().uuid(),
  name: leadStatsNameSchema,
  ghlPipelineNames: z.array(ghlNameSchema).min(1).max(LEAD_STATS_NAMES_MAX),
})
export type LeadStatsPipelineDto = z.infer<typeof leadStatsPipelineDtoSchema>

/**
 * What one sub-account had at its last read: its pipelines with their stages, and
 * its calendars. Stored on the connection (`statsCatalog`) by id and name, so a
 * definition by name can be matched against it.
 */
export const ghlStatsCatalogSchema = z.object({
  pipelines: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        stages: z.array(z.object({ id: z.string(), name: z.string() })),
      }),
    )
    .default([]),
  calendars: z
    .array(z.object({ id: z.string(), name: z.string() }))
    .default([]),
  /** The sub-account's time zone: the days of a venue that has none. */
  timeZone: z.string().nullable().default(null),
})
export type GhlStatsCatalog = z.infer<typeof ghlStatsCatalogSchema>

/**
 * What definitions can be chosen from, by name, across the connections in view
 * (a company's, or one venue's): the names the settings page offers and checks.
 */
export const leadStatsChoicesDtoSchema = z.object({
  pipelines: z.array(
    z.object({ name: z.string(), stages: z.array(z.string()) }),
  ),
  calendars: z.array(z.string()),
  tags: z.array(z.string()),
  sources: z.array(z.string()),
  /** False until at least one of these connections has been read. */
  read: z.boolean(),
  /** Whether every connection read could see calendars. */
  calendarsReadable: z.boolean(),
})
export type LeadStatsChoicesDto = z.infer<typeof leadStatsChoicesDtoSchema>

/** The company's lead stats settings. */
export const leadStatsSettingsDtoSchema = z.object({
  pipelines: z.array(leadStatsPipelineDtoSchema),
  metrics: z.array(leadStatsMetricDtoSchema),
  emailOff: z.boolean(),
  choices: leadStatsChoicesDtoSchema,
})
export type LeadStatsSettingsDto = z.infer<typeof leadStatsSettingsDtoSchema>

/**
 * One venue's adjustments, beside the company's definitions they replace. A null
 * adjustment means the venue uses the company's.
 */
export const venueLeadStatsSettingsDtoSchema = z.object({
  venueId: z.string().uuid(),
  pipelines: z.array(
    z.object({
      pipeline: leadStatsPipelineDtoSchema,
      ghlPipelineNames: z.array(ghlNameSchema).nullable(),
    }),
  ),
  metrics: z.array(
    z.discriminatedUnion('kind', [
      z.object({
        metric: leadStatsMetricDtoSchema,
        kind: z.literal('leads'),
        definition: leadsMetricDefinitionSchema.nullable(),
      }),
      z.object({
        metric: leadStatsMetricDtoSchema,
        kind: z.literal('appointments'),
        definition: appointmentsMetricDefinitionSchema.nullable(),
      }),
    ]),
  ),
  choices: leadStatsChoicesDtoSchema,
})
export type VenueLeadStatsSettingsDto = z.infer<
  typeof venueLeadStatsSettingsDtoSchema
>

/** What a refresh queued. */
export const leadStatsRefreshDtoSchema = z.object({
  connections: z.number().int().nonnegative(),
})
export type LeadStatsRefreshDto = z.infer<typeof leadStatsRefreshDtoSchema>
